import React from "react";
import { Link } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";

const SECTIONS = [
  {
    title: "General",
    items: [
      {
        q: "What is Massage Savings?",
        a: "Massage Savings is a flat-fee marketplace for massage deals. Massage therapists and businesses list discounted offers, and customers contact the therapist directly to book. Unlike Groupon, the platform takes 0% of every redemption — therapists keep 100% of what their customers pay.",
      },
      {
        q: "How is this different from Groupon or LivingSocial?",
        a: "Two big differences: (1) we don't take a commission on any deal — providers keep every dollar; (2) we charge providers a flat monthly listing fee ($49.99 individual, $99.99 business) per deal instead of slicing each transaction. The result: better margins for therapists, often better prices for customers.",
      },
      {
        q: "Is Massage Savings available in my area?",
        a: "Yes — we're a nationwide marketplace. Listings are filtered by ZIP code and city, so coverage depends on how many therapists in your area have published deals. If you can't find someone, use our \"Need a Therapist?\" form and we'll match you with someone in your area.",
      },
    ],
  },
  {
    title: "For Customers",
    items: [
      {
        q: "Do I pay Massage Savings or the therapist?",
        a: "You pay the therapist directly — by phone, email, or their booking link. Massage Savings never touches your money for the actual massage. The deal price you see on the listing is exactly what you pay the provider.",
      },
      {
        q: "How do I redeem a deal?",
        a: "Open the deal, then use one of the contact methods the therapist provides — call them, email them, or click their booking URL. Mention you saw the deal on Massage Savings. They'll confirm pricing and schedule your appointment.",
      },
      {
        q: "What if I'm not happy with my massage?",
        a: "Service refunds are handled directly by the provider — they set their own cancellation and refund policy. If you have a serious concern (no-show, misleading deal, safety issue), please contact us and we'll investigate.",
      },
      {
        q: "Can I leave a review?",
        a: "Yes! After your session, log in to your customer account, find the deal, and leave a star rating + comment. Reviews help your community find great therapists and help honest providers stand out.",
      },
      {
        q: "Is the customer account free?",
        a: "Completely free. You never pay Massage Savings anything as a customer.",
      },
    ],
  },
  {
    title: "For Therapists & Businesses",
    items: [
      {
        q: "How much does it cost to list a deal?",
        a: "Individual therapists: $49.99/month per deal. Massage businesses or spas: $99.99/month per deal. Every new deal gets a free 3-day trial after admin approval — no card required to start.",
      },
      {
        q: "Does Massage Savings take a cut of my deals?",
        a: "No. Zero percent. Every dollar your customer pays goes directly to you. The only thing you pay us is the flat monthly listing fee.",
      },
      {
        q: "What's the 3-day free trial?",
        a: "When your deal is approved by our team, it goes live for 3 days at no cost — no card required upfront. This gives you time to see customer interest before subscribing. If you want it to keep showing publicly after the trial ends, just click \"Subscribe to relist\" in your dashboard.",
      },
      {
        q: "What happens after I subscribe?",
        a: "Your deal stays live for 30 days. Before the 30 days end, you can renew by clicking subscribe again. Failed renewals just mean your deal stops showing publicly — nothing breaks, you can re-subscribe anytime.",
      },
      {
        q: "Can I have multiple deals?",
        a: "Yes — each deal is priced separately ($49.99 or $99.99/month each). Many providers run several different deals (e.g. a 60-min Swedish, a 90-min hot stone, a couples package).",
      },
      {
        q: "What is \"Featured\" / pin to top?",
        a: "A one-time $19 add-on that pins your deal to the top of search results for 7 days. Available from your dashboard on any live deal. Great for launches or seasonal promotions.",
      },
      {
        q: "What does \"Permanently free\" mean?",
        a: "Reserved for special partnerships granted directly by the platform owner — a deal stays live with no subscription required. This is not available for purchase or self-service.",
      },
      {
        q: "How do I get approved faster?",
        a: "Most listings are reviewed within 24 hours. To speed it up: use a clear deal title, write at least 2–3 sentences in the description, upload a high-quality photo, and double-check your contact info is real and reachable.",
      },
      {
        q: "Can I edit or delete my deal later?",
        a: "Delete: anytime, from your dashboard. Edit: contact us; substantive edits are reviewed again to keep listings honest.",
      },
      {
        q: "What payment methods are accepted?",
        a: "All major credit cards and debit cards via Stripe. Apple Pay and Google Pay where supported by your browser.",
      },
    ],
  },
  {
    title: "Payments & Refunds",
    items: [
      {
        q: "Is my payment secure?",
        a: "Yes. All payments are processed by Stripe — the same payments processor used by Shopify, Lyft, Instacart, and millions of businesses. We never see or store your card details.",
      },
      {
        q: "Can I get a refund on my subscription?",
        a: "Subscription fees are non-refundable except at our discretion. If you have an issue (accidental charge, technical problem, etc.), contact us — we'll review it case by case.",
      },
      {
        q: "Can I cancel my subscription?",
        a: "Yes, anytime. Your deal will stop being publicly visible at the end of the current 30-day paid period. There's no contract — you only pay when you want your deal published.",
      },
    ],
  },
  {
    title: "Account & Privacy",
    items: [
      {
        q: "I forgot my password — how do I reset it?",
        a: "Go to /forgot-password (or click \"Forgot password?\" on the login page). Enter your email and we'll send you a one-time reset link valid for 1 hour.",
      },
      {
        q: "How is my data protected?",
        a: "We collect only what's needed to run the marketplace — your name, email, role, and (for providers) your deal content and contact info. Payments go through Stripe; we never store card data. See our full Privacy Policy for details.",
      },
      {
        q: "Will my email be shared with customers or therapists?",
        a: "Only if you choose to publish it as a contact method on a deal listing, or include it in a \"Need a Therapist?\" request. Account emails are not exposed to anyone else.",
      },
    ],
  },
];

export default function Faq() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60]">Help</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl tracking-tight mt-2">Frequently asked questions</h1>
      <p className="text-[#5F6C60] mt-3 leading-relaxed">
        Quick answers about pricing, the free trial, payments, refunds, and how the marketplace works. Can't find your answer? <Link to="/contact" className="text-[#D97757] hover:underline">Contact us</Link>.
      </p>

      <div className="mt-12 space-y-10">
        {SECTIONS.map((sec) => (
          <section key={sec.title}>
            <h2 className="font-serif-display text-2xl tracking-tight mb-4">{sec.title}</h2>
            <Accordion type="single" collapsible className="bg-white border border-[#E8E2D6] rounded-2xl divide-y divide-[#E8E2D6]">
              {sec.items.map((it, i) => (
                <AccordionItem key={i} value={`${sec.title}-${i}`} className="px-5 border-b-0">
                  <AccordionTrigger data-testid={`faq-q-${sec.title.toLowerCase().replace(/\s+/g, '-')}-${i}`} className="text-left font-medium text-[#2C352D] hover:no-underline">
                    {it.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#5F6C60] leading-relaxed pb-5">
                    {it.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}
      </div>

      <div className="mt-16 p-8 bg-[#0F766E]/8 border border-[#0F766E]/25 rounded-2xl text-center">
        <h3 className="font-serif-display text-2xl text-[#0F766E]">Still have questions?</h3>
        <p className="text-sm text-[#2C352D] mt-2">We reply to every message within 1 business day.</p>
        <Link to="/contact">
          <Button data-testid="faq-cta-contact" className="mt-5 rounded-full bg-[#D97757] hover:bg-[#C26243] text-white h-12 px-6">Contact us</Button>
        </Link>
      </div>
    </div>
  );
}
